// src/components/context/ProfileContext.jsx
"use client";
import React, { createContext, useContext, useState } from "react";
import { BASE_URL } from "../config/apiconfig";

const ProfileContext = createContext();
export const useProfile = () => useContext(ProfileContext);

export const ProfileProvider = ({ children }) => {
  const [profileData, setProfileData] = useState(null); // start as null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ===================== GET PROFILE =====================
  const getProfile = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const res = await fetch(`${BASE_URL}/profile/${userId}`, {
        credentials: "include", // send the httpOnly auth cookie
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error("Failed to fetch profile");
      }

      const data = await res.json();
      setProfileData(data.data); // update state
      return { success: true, data: data.data };
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  // ===================== UPDATE PROFILE =====================
  const updateProfile = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const res = await fetch(
        `${BASE_URL}/profile/${profileData.id}/update`,
        {
          method: "POST",
          credentials: "include", // send the httpOnly auth cookie
          headers: {
            Authorization: `Bearer ${token}`,
            // ❌ DO NOT set Content-Type
          },
          body: formData,
        }
      );

      if (!res.ok) {
        const text = await res.text();

        // Try to parse the error response to get specific error messages
        let errorMessage = "Failed to update profile";
        try {
          const errorData = JSON.parse(text);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If parsing fails, use the raw text or default message
          errorMessage = text || "Failed to update profile";
        }

        throw new Error(errorMessage);
      }

      const data = await res.json();
      setProfileData((prev) => ({ ...prev, ...data.data }));

      return { success: true, data: data.data };
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profileData,
        setProfileData,
        getProfile,
        updateProfile,
        loading,
        error,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
